import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpLeft, BookOpenCheck, BrainCircuit, GraduationCap, HeartHandshake, Lock, Sparkles, User } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`أهلاً ${u.name}`);
      navigate(u.role === "counselor" ? "/counselor" : "/supervisor");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell min-h-screen" dir="rtl">
      <header className="login-topbar">
        <div className="login-topbar-schoolmarks">
          <img src="/branding/school-abaq.png" alt="شعار مدرسة عبق العلم" />
          <img src="/branding/school-durat.png" alt="شعار مدرسة درة الشرق" />
        </div>
        <img
          src="/branding/kayan-mark.png"
          alt="شعار كيان"
          data-testid="kayan-logo-banner"
          className="login-topbar-logo"
        />
      </header>

      <main className="login-stage">
        <section className="login-story" aria-label="عن منصة الإرشاد الطلابي">
          <div className="login-story-kicker"><Sparkles /> إرشاد أذكى، أثر أبقى</div>
          <h1>
            نرى إمكانات
            <span>كل طالب.</span>
          </h1>
          <p className="login-story-description">
            منصة واحدة تربط الملاحظة بالرعاية، وتحوّل تفاصيل اليوم الدراسي إلى خطوات واضحة لنمو الطالب.
          </p>
          <div className="login-journey" aria-label="رحلة الدعم الطلابي">
            <div className="login-journey-line" />
            <div className="login-journey-step">
              <span className="login-journey-icon"><BookOpenCheck /></span>
              <strong>نفهم</strong>
              <small>نرصد الاحتياج</small>
            </div>
            <div className="login-journey-step login-journey-step-active">
              <span className="login-journey-icon"><BrainCircuit /></span>
              <strong>نرشد</strong>
              <small>نختار الخطوة</small>
            </div>
            <div className="login-journey-step">
              <span className="login-journey-icon"><HeartHandshake /></span>
              <strong>ننمو</strong>
              <small>نقيس الأثر</small>
            </div>
          </div>
          <div className="login-story-note">
            <span className="login-note-symbol"><GraduationCap /></span>
            <div>
              <strong>لكل طالب مساره الخاص</strong>
              <span>والرعاية الجيدة تبدأ بخطوة واعية.</span>
            </div>
            <ArrowUpLeft className="login-note-arrow" />
          </div>
          <div className="login-story-footer">
            <span>المشرف الإرشادي آدم المحنبي</span>
            <span>© {new Date().getFullYear()} شركة كيان النموذجية</span>
          </div>
        </section>

        <section className="login-form-panel" aria-label="تسجيل الدخول">
          <div className="login-form-heading">
            <span className="login-form-mark"><Lock /></span>
            <div>
              <div className="login-eyebrow">بوابتك إلى المنصة</div>
              <h2>مرحباً بعودتك</h2>
              <p>سجّل الدخول لمتابعة أعمال الإرشاد الطلابي.</p>
            </div>
          </div>
          <form onSubmit={submit} className="login-form-fields" data-testid="login-form">
            <div>
              <Label htmlFor="username" className="login-label">اسم المستخدم</Label>
              <div className="login-input-wrap relative">
                <User className="login-field-icon" />
                <Input
                  id="username"
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="login-field"
                  placeholder="أدخل اسم المستخدم"
                  required
                  autoComplete="username"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="login-label">كلمة المرور</Label>
              <div className="login-input-wrap relative">
                <Lock className="login-field-icon" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="login-field"
                  placeholder="أدخل كلمة المرور"
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="login-submit w-full"
            >
              {loading ? "جاري الدخول..." : "دخول إلى المنصة"}
              {!loading && <ArrowUpLeft className="h-4 w-4" />}
            </Button>
          </form>
          <div className="login-form-bottom">
            <span><span className="login-secure-dot" /> دخول آمن ومخصص لحسابك</span>
            <span>منصة الإرشاد الطلابي</span>
          </div>
        </section>
      </main>

      <footer className="login-mobile-footer">
        <img src="/branding/school-abaq.png" alt="شعار مدرسة عبق العلم" />
        <span>عبق العلم</span>
        <i />
        <img src="/branding/school-durat.png" alt="شعار مدرسة درة الشرق" />
        <span>درة الشرق</span>
        <b>شركة كيان النموذجية</b>
      </footer>
    </div>
  );
}
